import { useMutation } from "@apollo/client";
import { PrimaryFullScreenSpinner, Txt, toast, useAppForm } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

const formSchema = z.object({
  code: z.string().length(6).regex(/^\d+$/),
});

type FormData = z.input<typeof formSchema>;

export const Route = createFileRoute("/__auth/mfa-test")({
  head: () => opsHead("MFA Test"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const [createMfaVerificationMutation] = useMutation(
    graphql(`
      mutation TestMfaVerification($input: CreateMfaVerificationInput!) {
        createMfaVerification(input: $input) {
          verified
          mfaVerification {
            token
            expiresAt
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {} as FormData,
    validators: {
      onChange: formSchema,
      onMount: formSchema,
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsedValueResult = formSchema.safeParse(value);
      if (!parsedValueResult.success) {
        throw new Error("bad form data");
      }
      await createMfaVerificationMutation({
        variables: {
          input: {
            totpCode: parsedValueResult.data.code,
          },
        },
        onCompleted: (data) => {
          if (!data.createMfaVerification.verified) {
            toast({
              kind: "error",
              title: "Failed to validate code.",
            });
            throw new Error("failed to validate code");
          } else {
            toast({
              kind: "success",
              title: "Validated code; MFA validation now on record.",
            });
          }
        },
        onError: (error) => {
          toast({
            kind: "error",
            title: "Failed to validate code.",
          });
          throw error;
        },
      });
    },
  });

  return (
    <AdminPageContainer>
      <AdminPageHeader title="MFA Verification Test" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="flex flex-col gap-6"
      >
        <form.AppForm>
          <form.AppField name="code">
            {(field) => (
              <field.InputOTPField
                autoFocus
                label={
                  <Txt size="sm" weight="semibold">
                    Two-step verification
                  </Txt>
                }
                description={
                  <Txt size="xs">
                    Please enter the verification code generated by your
                    authenticator app.
                  </Txt>
                }
              />
            )}
          </form.AppField>
          <div>
            <form.SubmitButton childrenWhileSubmitting="Submitting...">
              Submit
            </form.SubmitButton>
          </div>
        </form.AppForm>
      </form>
    </AdminPageContainer>
  );
}
